<script lang="ts">
import type { AcceptableValue, ComboboxContentEmits, ComboboxContentProps, ComboboxRootEmits, ComboboxRootProps } from 'reka-ui'
import type { UseComponentIconsProps } from '../composables/useComponentIcons'
import type { ArrayOrNested, EmitsToProps, GetItemKeys, GetItemValue, GetModelValue, GetModelValueEmits, NestedItem } from '../types/utils'
import type { ListItem } from './AwListItem.vue'
import type { InputProps } from './AwTextField.vue'
import { buttonGroupVariantUi } from './AwButtonGroup.vue'
import { ui as listItemUi } from './AwListItem.vue'

export type SelectMenuItem = ListItem | AcceptableValue | boolean

export interface SelectMenuProps<T extends ArrayOrNested<SelectMenuItem> = ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<T> | undefined = undefined, M extends boolean = false> extends Pick<ComboboxRootProps<T>, 'open' | 'defaultOpen' | 'disabled' | 'name' | 'resetSearchTermOnBlur' | 'resetSearchTermOnSelect' | 'highlightOnHover'>, UseComponentIconsProps {
  id?: string
  placeholder?: string
  searchInput?: boolean | InputProps
  size?: 'sm' | 'md' | 'lg'
  color?: 'default' | 'critical'
  trailingIcon?: string
  content?: Omit<ComboboxContentProps, 'as' | 'asChild' | 'forceMount'> & Partial<EmitsToProps<ComboboxContentEmits>>
  portal?: boolean | string | HTMLElement
  valueKey?: VK
  labelKey?: keyof NestedItem<T>
  items?: T
  canRemoveAll?: boolean
  defaultValue?: GetModelValue<T, VK, M>
  modelValue?: GetModelValue<T, VK, M>
  multiple?: M & boolean
  createItem?: boolean | 'always' | { position?: 'top' | 'bottom', when?: 'empty' | 'always' }
  filterFields?: string[]
  ignoreFilter?: boolean
  maxVisibleItems?: number | 'none'
  class?: string | object
  contentClass?: string | object
  /**
   * When `multiple` is true, marks an item as selected in the list even if its value is not
   * in `modelValue` (e.g. global “select all” represented by a synthetic token in the model).
   */
  isItemSelected?: (item: NestedItem<T>) => boolean
}

export type SelectMenuEmits<A extends ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<A> | undefined, M extends boolean> = Pick<ComboboxRootEmits, 'update:open'> & {
  'trailingClick': [payload: Event]
  'change': [payload: Event]
  'blur': [payload: FocusEvent]
  'focus': [payload: FocusEvent]
  'create': [item: string]
  'update:model-value': [payload: GetModelValue<A, VK, M>]
} & GetModelValueEmits<A, VK, M>

type SlotProps<T extends SelectMenuItem> = (props: { item: T, index: number }) => any

export interface SelectMenuSlots<
  A extends ArrayOrNested<SelectMenuItem> = ArrayOrNested<SelectMenuItem>,
  VK extends GetItemKeys<A> | undefined = undefined,
  M extends boolean = false,
  T extends NestedItem<A> = NestedItem<A>,
> {
  'default': (props: {
    modelValue?: GetModelValue<A, VK, M>
    open: boolean
  }) => any
  'empty': (props: { searchTerm?: string }) => any
  'before-options': () => any
  'item': SlotProps<T>
  'item-leading': SlotProps<T>
  'item-label': SlotProps<T>
  'item-trailing': SlotProps<T>
  'create-item-label': (props: { item: string }) => any
  'search-trailing': () => any
}

const ui = {
  input: 'border-b border-secondary-400',
  focusScope: 'flex flex-col min-h-0',
  base: 'relative group rounded-md inline-flex items-center focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100 text-secondary-900 disabled:text-gray-600 transition-colors text-md gap-1.5 text-highlighted pe-9',
  color: {
    default: 'bg-white border border-secondary-400 not-disabled:hover:border-secondary-700 data-[state=open]:border-secondary-800 data-[state=open]:ring-1 data-[state=open]:ring-secondary-200 data-[state=open]:hover:border-secondary-800',
    critical: 'bg-critical-50 border border-critical-500 not-disabled:hover:border-critical-600 data-[state=open]:border-critical-600 data-[state=open]:ring-1 data-[state=open]:ring-critical-200 data-[state=open]:hover:border-critical-600',
  },
  value: 'truncate pointer-events-none text-md',
  placeholder: 'truncate text-gray text-md',
  trailing: 'absolute inset-y-0 end-0 flex items-center me-3',
  trailingIcon: 'shrink-0 text-dimmed size-4 text-secondary-600',
  removeAllWrapper: 'group/removeAll hover:bg-gray-200 rounded-md size-fit p-0.5 me-2.5 place-self-center absolute inset-y-0 end-0',
  removeAllIcon: 'shrink-0 text-dimmed size-4 text-secondary-600 group-hover/removeAll:text-gray-800',
  content: 'max-h-60 w-[--reka-combobox-trigger-width] bg-white shadow-lg rounded-lg border border-secondary-400 overflow-hidden data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-[--reka-select-content-transform-origin] pointer-events-auto flex flex-col',
  viewport: 'relative overflow-y-auto flex-1',
  beforeOptions: 'shrink-0',
  group: 'isolate',
  empty: 'flex items-center justify-center gap-1 text-md text-center text-secondary-700 text-muted h-150px',
  separator: '-mx-1 my-1 h-px bg-secondary-400',
  itemTrailing: 'ms-auto inline-flex gap-1.5 items-center',
  itemTrailingIcon: 'shrink-0 size-3.5 text-primary-600',
  badgeWrapper: 'flex flex-wrap gap-1.25 max-w-full',
  checkbox: 'group-data-[state=checked]:hidden!',
  loading: {
    base: 'w-full absolute h-full flex items-center justify-center text-secondary-700 text-muted bg-white/70 z-1',
    wrapper: 'flex items-center gap-1 text-secondary-700',
    icon: 'animate-spin size-4',
    text: 'text-md',
  },
  listItem: {
    root: 'bg-secondary-50 text-md text-secondary-800 py-0!',
    base: 'bg-transparent! py-1.5!',
  },
  item: {
    root: 'flex flex-col',
    wrapper: 'flex items-center gap-1.5',
  },
  closeIcon: 'text-secondary-600 size-3 cursor-pointer',
  size: {
    sm: {
      default: 'px-2 py-1.5',
      selected: '',
    },
    md: {
      default: 'px-3 py-2',
      selected: 'px-3 py-1.25',
    },
    lg: {
      default: 'px-4 py-2.5',
      selected: '',
    },
  },
  ...buttonGroupVariantUi,
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<T> | undefined = undefined, M extends boolean = false">
import { createReusableTemplate, reactivePick } from '@vueuse/core'
import { defu } from 'defu'
import { ComboboxAnchor, ComboboxContent, ComboboxEmpty, ComboboxGroup, ComboboxInput, ComboboxItem, ComboboxItemIndicator, ComboboxLabel, ComboboxPortal, ComboboxRoot, ComboboxSeparator, ComboboxTrigger, FocusScope, useFilter, useForwardPropsEmits } from 'reka-ui'
import { computed, ref, toRaw, toRef, useTemplateRef } from 'vue'
import { useButtonGroup } from '../composables/useButtonGroup'
import { useComponentIcons } from '../composables/useComponentIcons'
import { usePortal } from '../composables/usePortal'
import icons from '../styles/icons'
import { compare, get, isArrayOfArray } from '../utils'
import AwBadge from './AwBadge.vue'
import AwCheckbox from './AwCheckbox.vue'
import AwIcon from './AwIcon.vue'
import AwTextField from './AwTextField.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<SelectMenuProps<T, VK, M>>(), {
  portal: true,
  searchInput: true,
  labelKey: 'label' as never,
  resetSearchTermOnBlur: true,
  resetSearchTermOnSelect: true,
  size: 'md',
  color: 'default',
  maxVisibleItems: 2,
  canRemoveAll: true,
})
const emits = defineEmits<SelectMenuEmits<T, VK, M>>()
defineSlots<SelectMenuSlots<T, VK, M>>()

const searchTerm = defineModel<string>('searchTerm', { default: '' })

const { contains } = useFilter({ sensitivity: 'base' })

const rootProps = useForwardPropsEmits(reactivePick(props, 'modelValue', 'defaultValue', 'open', 'defaultOpen', 'multiple', 'resetSearchTermOnBlur', 'resetSearchTermOnSelect', 'highlightOnHover'), emits)
const portalProps = usePortal(toRef(() => props.portal))
const contentProps = toRef(() => defu(props.content, { side: 'bottom', sideOffset: 8, collisionPadding: 8, position: 'popper' }) as ComboboxContentProps)
const searchInputProps = toRef(() => defu(props.searchInput, { placeholder: 'Rechercher...' }) as InputProps)

const searchInputRef = useTemplateRef<{ inputRef: HTMLInputElement }>('searchInputRef')
const optionsViewportRef = useTemplateRef<HTMLElement>('optionsViewportRef')

defineExpose({
  /** Scrollable list container inside the dropdown (for preserving scroll when items reorder). */
  getOptionsViewport: (): HTMLElement | null => optionsViewportRef.value ?? null,
  /** Renders a value as the label shown on the trigger. */
  displayValue,
  /** Puts the caret back in the search field: reka-ui gives every option a tabindex. */
  focusSearchInput: () => searchInputRef.value?.inputRef.focus(),
})

const { orientation, hasButtonGroup, size: buttonGroupSize } = useButtonGroup<InputProps>(props)
// The menu only renders a trailing icon: Vue casts an absent isTrailing to false, which would send the loader to the leading side
const { isTrailing, trailingIconName } = useComponentIcons(toRef(() => ({ ...props, isTrailing: true, trailingIcon: props.trailingIcon ?? icons.chevronDown })))

const [DefineCreateItemTemplate, ReuseCreateItemTemplate] = createReusableTemplate()

const inputSize = computed(() => buttonGroupSize.value)
const color = computed(() => props.color ?? 'default')

const unchecked = ref(false)

function isItemValueList(value: unknown): value is GetItemValue<T, VK>[] {
  return Array.isArray(value)
}

function displayValue(value: GetItemValue<T, VK> | GetItemValue<T, VK>[]): string | undefined {
  if (props.multiple && Array.isArray(value)) {
    const values = value.map(v => displayValue(v)).filter(Boolean)
    return values?.length ? values.join(', ') : undefined
  }
  if (!props.valueKey) {
    return value && (typeof value === 'object' ? get(value, props.labelKey as string) : value)
  }
  const item = items.value.find(item => compare(typeof item === 'object' ? get(item as Record<string, any>, props.valueKey as string) : item, value))
  return item && (typeof item === 'object' ? get(item, props.labelKey as string) : item)
}

const groups = computed<SelectMenuItem[][]>(() =>
  props.items?.length
    ? isArrayOfArray(props.items)
      ? props.items
      : [props.items]
    : [],
)
const items = computed(() => groups.value.flatMap(group => group) as T[])
const filteredGroups = computed(() => {
  if (props.ignoreFilter || !searchTerm.value) {
    return groups.value
  }
  const fields = Array.isArray(props.filterFields) ? props.filterFields : [props.labelKey] as string[]
  return groups.value.map(items => items.filter((item) => {
    if (typeof item !== 'object' || item === null)
      return contains(String(item), searchTerm.value)
    if (item.type && ['label', 'separator'].includes(item.type))
      return true
    return fields.some((field) => {
      const value = get(item, field)
      return value !== undefined && value !== null && contains(String(value), searchTerm.value)
    })
  })).filter(group => group.filter(item =>
    !isSelectItem(item) || (!item.type || !['label', 'separator'].includes(item.type)),
  ).length > 0)
})
const filteredItems = computed(() => filteredGroups.value.flatMap(group => group))
const createItem = computed(() => {
  if (!props.createItem || !searchTerm.value)
    return false
  const newItem = props.valueKey ? { [props.valueKey]: searchTerm.value } as NestedItem<T> : searchTerm.value
  if ((typeof props.createItem === 'object' && props.createItem.when === 'always') || props.createItem === 'always')
    return !filteredItems.value.find(item => compare(item, newItem, props.valueKey as string))
  return !filteredItems.value.length
})
const createItemPosition = computed(() => typeof props.createItem === 'object' ? props.createItem.position : 'bottom')

function onUpdate(value: any) {
  if (toRaw(props.modelValue) === value) {
    return
  }
  // @ts-expect-error - 'target' does not exist in type 'EventInit'
  const event = new Event('change', { target: { value } })
  emits('change', event)

  if (props.resetSearchTermOnSelect)
    searchTerm.value = ''
}

function onUpdateOpen(value: boolean) {
  let timeoutId
  if (!value) {
    const event = new FocusEvent('blur')
    emits('blur', event)
    if (props.resetSearchTermOnBlur) {
      const STATE_ANIMATION_DELAY_MS = 100
      timeoutId = setTimeout(() => {
        searchTerm.value = ''
      }, STATE_ANIMATION_DELAY_MS)
    }
  }
  else {
    const event = new FocusEvent('focus')
    emits('focus', event)
    clearTimeout(timeoutId)
  }
}

function onSelect(e: Event, item: SelectMenuItem) {
  if (!isSelectItem(item))
    return
  if (item.disabled) {
    e.preventDefault()
    return
  }
  item.onClick?.(e)
}

function onRemove(originalValue: any) {
  if (!props.multiple || !Array.isArray(props.modelValue))
    return
  const newValue = props.modelValue.filter((item: any) => {
    if (!props.valueKey && (typeof item !== 'object' || item === null)) {
      return item !== originalValue
    }
    if (props.valueKey) {
      const itemValue = typeof item === 'object' ? get(item as Record<string, any>, props.valueKey as string) : item
      const originalVal = typeof originalValue === 'object' ? get(originalValue as Record<string, any>, props.valueKey as string) : originalValue
      return !compare(itemValue, originalVal)
    }
    return !compare(item, originalValue)
  })
  emits('update:model-value', newValue)
  onUpdate(newValue)
}

function isSelectItem(item: SelectMenuItem): item is ListItem {
  return typeof item === 'object' && item !== null
}

function isItemDisabled(item: SelectMenuItem): boolean {
  return isSelectItem(item) && Boolean(item.disabled)
}

function comboboxItemValue(item: SelectMenuItem): unknown {
  if (!isSelectItem(item))
    return item
  return props.valueKey ? get(item as Record<string, unknown>, props.valueKey as string) : item
}

function isItemInModelValue(item: SelectMenuItem): boolean {
  if (!props.multiple || !Array.isArray(props.modelValue))
    return false
  const itemValue = comboboxItemValue(item)
  return props.modelValue.some((v: unknown) => compare(v as never, itemValue as never))
}

function itemFullySelected(item: SelectMenuItem): boolean {
  if (isItemInModelValue(item))
    return true
  if (props.isItemSelected === undefined || !isSelectItem(item))
    return false
  return Boolean(props.isItemSelected(item as NestedItem<T>))
}

function onClearSearch() {
  searchTerm.value = ''
  searchInputRef.value?.inputRef.focus()
}
</script>

<!-- eslint-disable vue/no-template-shadow -->
<template>
  <DefineCreateItemTemplate>
    <ComboboxGroup :class="[ui.group, listItemUi.root]">
      <ComboboxItem
        :class="[listItemUi.base, listItemUi.color.primary, listItemUi.cursor]"
        :value="searchTerm"
        @select.prevent="emits('create', searchTerm)"
      >
        <span :class="listItemUi.itemLabel">
          <slot name="create-item-label" :item="searchTerm">
            <div class="flex items-center gap-1">
              <AwIcon :name="icons.plus" class="size-3.5" />
              <span>Créer "{{ searchTerm }}"</span>
            </div>
          </slot>
        </span>
      </ComboboxItem>
    </ComboboxGroup>
  </DefineCreateItemTemplate>
  <ComboboxRoot
    :id
    v-slot="{ modelValue, open }"
    v-bind="{ ...rootProps, ...$attrs }"
    ignore-filter
    as-child
    :name
    :disabled
    @update:model-value="onUpdate"
    @update:open="onUpdateOpen"
  >
    <ComboboxAnchor as-child>
      <ComboboxTrigger :class="[ui.base, ui.color[color], props.class, hasButtonGroup && ui.buttonGroup[orientation], (multiple && Array.isArray(modelValue) && !!modelValue.length) ? ui.size[inputSize].selected : ui.size[inputSize].default]" tabindex="0">
        <slot :model-value="(modelValue as GetModelValue<T, VK, M>)" :open="open">
          <template v-for="displayedModelValue in [displayValue(modelValue as GetModelValue<T, VK, M>)]" :key="displayedModelValue">
            <div v-if="displayedModelValue !== undefined && displayedModelValue !== null && multiple && isItemValueList(modelValue)" :class="ui.badgeWrapper">
              <template v-for="(originalValue, index) in modelValue" :key="index">
                <AwBadge
                  v-if="maxVisibleItems === 'none' || index < maxVisibleItems"
                  :key="index"
                  :label="displayValue(originalValue)"
                  :is-removable="!disabled"
                  size="sm"
                  class="max-w-full"
                  :class="{ 'bg-gray-400 border-gray-400 text-gray-800 cursor-not-allowed': disabled }"
                  @remove="onRemove(originalValue)"
                />
              </template>
              <AwBadge
                v-if="maxVisibleItems !== 'none' && modelValue.length > maxVisibleItems"
                :label="`+${modelValue.length - maxVisibleItems}`"
                size="sm"
                class="max-w-full"
              />
            </div>
            <span v-else-if="displayedModelValue !== undefined && displayedModelValue !== null" :class="ui.value">
              {{ displayedModelValue }}
            </span>
            <span v-else :class="ui.placeholder">
              {{ placeholder ?? '&nbsp;' }}
            </span>
          </template>
        </slot>
        <span v-if="isTrailing && canRemoveAll && (multiple && Array.isArray(modelValue) && modelValue.length > 1)" :class="ui.removeAllWrapper" :model-value="modelValue">
          <AwIcon name="i-ph-x-bold" :model-value="modelValue" :class="ui.removeAllIcon" @click.stop="emits('update:model-value', [] as GetModelValue<T, VK, M>)" />
        </span>
        <span v-if="isTrailing && (!canRemoveAll || !(multiple && Array.isArray(modelValue) && modelValue.length > 1))" :class="ui.trailing">
          <AwIcon v-if="trailingIconName" :name="trailingIconName" :class="[ui.trailingIcon, isLoading && 'animate-spin']" @click="emits('trailingClick', $event)" />
        </span>
      </ComboboxTrigger>
    </ComboboxAnchor>
    <ComboboxPortal v-bind="portalProps">
      <ComboboxContent :class="[ui.content, props.contentClass]" v-bind="contentProps">
        <FocusScope trapped :class="ui.focusScope">
          <ComboboxInput v-if="!!searchInput" v-model="searchTerm" :display-value="() => searchTerm" as-child>
            <AwTextField
              ref="searchInputRef"
              autofocus
              autocomplete="off"
              :leading-icon="icons.search"
              is-borderless
              v-bind="searchInputProps"
              :class="ui.input"
            >
              <template #trailing>
                <slot name="search-trailing" />
                <AwIcon v-show="searchTerm" :name="icons.closeBold" :class="ui.closeIcon" @click="onClearSearch" />
              </template>
            </AwTextField>
          </ComboboxInput>
          <div v-if="$slots['before-options']" :class="ui.beforeOptions">
            <slot name="before-options" />
          </div>
          <ComboboxEmpty :class="ui.empty">
            <slot name="empty" :search-term="searchTerm">
              <div v-if="isLoading" :class="ui.loading.wrapper">
                <AwIcon :name="icons.loading" :class="ui.loading.icon" />
                <p :class="ui.loading.text">
                  Rechercher en cours....
                </p>
              </div>
              <div v-else>
                {{ searchTerm ? `Aucun résultat pour ${searchTerm}` : 'Aucun résultat' }}
              </div>
            </slot>
          </ComboboxEmpty>
          <div ref="optionsViewportRef" role="presentation" :class="ui.viewport">
            <div v-if="isLoading" :class="ui.loading.base">
              <AwIcon :name="icons.loading" :class="ui.loading.icon" />
            </div>
            <ReuseCreateItemTemplate v-if="createItem && createItemPosition === 'top'" />
            <ComboboxGroup v-for="(group, groupIndex) in filteredGroups" :key="`group-${groupIndex}`" :class="ui.group">
              <template
                v-for="(item, index) in group"
                :key="`group-${groupIndex}-${index}-${isSelectItem(item) && props.valueKey ? String(get(item as Record<string, any>, props.valueKey as string)) : index}-${isSelectItem(item) && item.disabled ? 1 : 0}`"
              >
                <div v-if="isSelectItem(item) && item.type === 'label'" :class="[listItemUi.root, ui.listItem.root]">
                  <ComboboxLabel :class="[listItemUi.base, ui.listItem.base]">
                    {{ get(item, props.labelKey as string) }}
                  </ComboboxLabel>
                </div>
                <ComboboxSeparator v-else-if="isSelectItem(item) && item.type === 'separator'" :class="ui.separator" />
                <div v-else :class="listItemUi.root">
                  <ComboboxItem
                    class="group" :class="[listItemUi.base, listItemUi.color.primary, listItemUi.cursor]"
                    :disabled="isSelectItem(item) && item.disabled"
                    :value="props.valueKey && isSelectItem(item) ? get(item, props.valueKey as string) : item"
                    @select="onSelect($event, item)"
                  >
                    <slot name="item" :item="(item as NestedItem<T>)" :index="index">
                      <div :class="ui.item.root">
                        <div :class="ui.item.wrapper">
                          <template v-if="multiple">
                            <template v-if="itemFullySelected(item)">
                              <AwCheckbox is-checked :disabled="isItemDisabled(item)" :class="listItemUi.itemLeadingIcon" />
                            </template>
                            <template v-else>
                              <AwCheckbox v-model="unchecked" :disabled="isItemDisabled(item)" :class="[ui.checkbox, listItemUi.itemLeadingIcon]" @change="unchecked = false" />
                              <ComboboxItemIndicator as-child>
                                <AwCheckbox is-checked :disabled="isItemDisabled(item)" :class="listItemUi.itemLeadingIcon" />
                              </ComboboxItemIndicator>
                            </template>
                          </template>
                          <slot name="item-leading" :item="(item as NestedItem<T>)" :index="index">
                            <AwIcon v-if="isSelectItem(item) && item.icon" :name="item.icon" :class="listItemUi.itemLeadingIcon" />
                          </slot>
                          <slot name="item-label" :item="(item as NestedItem<T>)" :index="index">
                            {{ isSelectItem(item) ? get(item, props.labelKey as string) : item }}
                          </slot>
                        </div>
                        <span v-if="isSelectItem(item) && item.helperText" :class="listItemUi.helperText">
                          {{ item.helperText }}
                        </span>
                      </div>
                      <span :class="ui.itemTrailing">
                        <slot name="item-trailing" :item="(item as NestedItem<T>)" :index="index" />
                        <ComboboxItemIndicator v-if="!multiple" as-child>
                          <AwIcon :name="icons.check" :class="ui.itemTrailingIcon" />
                        </ComboboxItemIndicator>
                      </span>
                    </slot>
                  </ComboboxItem>
                </div>
              </template>
            </ComboboxGroup>
            <ReuseCreateItemTemplate v-if="createItem && createItemPosition === 'bottom'" />
          </div>
        </FocusScope>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>
